import { supabase } from '@/integrations/supabase/client';

interface CachedRangeData {
  participantRanges: any[];
  thumbnails: Record<string, string[]>;
}

// Global cache keyed by `${raceId}:${eventSwissId}`
const cache = new Map<string, CachedRangeData>();

// In-flight promises for deduplication
const inflight = new Map<string, Promise<CachedRangeData>>();

/**
 * Get participant ranges from cache or fetch once.
 * Deduplicates concurrent requests for the same key.
 */
export async function getParticipantRanges(
  raceId: string,
  eventSwissId: string,
  thumbnailSlice = 15
): Promise<CachedRangeData> {
  const key = `${raceId}:${eventSwissId}`;

  const cached = cache.get(key);
  if (cached) {
    console.log(`[participantRangesCache] HIT ${key}`);
    return cached;
  }

  // Reuse in-flight request
  const pending = inflight.get(key);
  if (pending) {
    console.log(`[participantRangesCache] DEDUP ${key}`);
    return pending;
  }

  console.log(`[participantRangesCache] FETCH ${key}`);
  const promise = (async (): Promise<CachedRangeData> => {
    try {
      const { data, error } = await supabase.functions.invoke('compute-participant-ranges', {
        body: { raceId, eventId: eventSwissId }
      });

      if (error || !data?.participants) {
        console.error('[participantRangesCache] Error:', error);
        return { participantRanges: [], thumbnails: {} };
      }

      const participantRanges: any[] = data.participants;
      const thumbnails: Record<string, string[]> = {};

      participantRanges.forEach((p: any) => {
        thumbnails[p.id] = p.photo_range?.urls?.slice(0, thumbnailSlice) || [];
      });

      const result: CachedRangeData = { participantRanges, thumbnails };
      cache.set(key, result);
      return result;
    } finally {
      inflight.delete(key);
    }
  })();

  inflight.set(key, promise);
  return promise;
}

/**
 * Synchronous cache lookup (no fetch). Returns null if not cached.
 */
export function getCachedRanges(raceId: string, eventSwissId: string): CachedRangeData | null {
  return cache.get(`${raceId}:${eventSwissId}`) ?? null;
}

/**
 * Get a specific participant's full photo data from cache.
 */
export function getCachedParticipantPhotos(
  raceId: string,
  eventSwissId: string,
  participantId: string
): { urls: string[]; fileIds: number[] } | null {
  const cached = cache.get(`${raceId}:${eventSwissId}`);
  if (!cached) return null;

  const pr = cached.participantRanges.find((r: any) => r.id === participantId);
  if (!pr?.photo_range?.urls) return null;

  return {
    urls: pr.photo_range.urls,
    fileIds: (pr.photo_range.file_ids || []).map(Number)
  };
}
